/*
 * Diamond Melee minimum-carat checkout gate — client-side mirror of
 * snippets/melee-checkout-blocked.liquid. Keep the two in sync.
 *
 * Liquid decides the state on page load, but cart.js only re-renders `.js-contents`
 * of the cart footer and cart-notification.js only re-renders the popup's product
 * block, so every checkout control would otherwise keep the state it was printed
 * with until a full page reload. This re-evaluates the gate on every cart update and
 * re-applies it to all checkout controls on the page.
 */
(function () {
  const MIN_TOTAL_CARAT = 1;

  // How long a single cart mutation may hold checkout shut before we assume its caller
  // died and let the shopper through again.
  const UPDATE_WATCHDOG_MS = 15000;

  // Every control that starts a checkout and therefore has to follow the gate.
  const CHECKOUT_CONTROLS = [
    '#checkout', // cart page footer
    '#cart-notification-form [name="checkout"]', // add-to-cart popup
    '#CartDrawer-Checkout', // cart drawer, if the cart type is ever switched to it
  ];

  // Third-party buttons (Shop Pay, PayPal, ...) can't be disabled, so they get hidden.
  const DYNAMIC_CHECKOUT_CONTAINERS = ['#MeleeDynamicCheckoutButtons'];

  // The "1 carat minimum order for melee diamonds" notice above each checkout control, telling the
  // shopper why it is disabled. See snippets/melee-minimum-note.liquid.
  const MINIMUM_NOTES = ['[data-melee-minimum-note]'];

  // Seeded by the Liquid gate in layout/theme.liquid so the first paint is already correct.
  let blocked = window.meleeCheckoutBlocked === true;
  let cartIsEmpty = window.meleeCartIsEmpty === true;

  // Number of cart mutations in flight. Checkout stays disabled while any is running:
  // between clicking remove (or changing a quantity) and the response landing, the state
  // on screen is known to be stale, and the customer must not be able to check out on it.
  let pendingUpdates = 0;
  let updateWatchdogs = [];

  // Mirrors Liquid's string-to-number cast: only a plain integer/decimal counts,
  // anything else is 0 (unlike parseFloat, which would read "0.5ct" as 0.5).
  function toNumber(value) {
    return /^-?\d+(\.\d+)?$/.test(value) ? parseFloat(value) : 0;
  }

  function isMelee(item) {
    const type = ((item && item.product_type) || '').toLowerCase().replace(/[\s\-_]/g, '');
    return type === 'diamondmelee';
  }

  // First word is the stone count, last word the per-stone weight ("... Carat Weight of 0.055").
  function lineCarat(item) {
    const words = ((item && item.title) || '').trim().split(/\s+/);
    const weight = toNumber((words[words.length - 1] || '').replace(/[.,;:]+$/, ''));
    let multiplier = toNumber(words[0]);
    if (multiplier <= 0) multiplier = 1;
    return weight * multiplier * (item.quantity || 0);
  }

  function isCartBlocked(items) {
    let hasMelee = false;
    let totalCarat = 0;

    items.forEach((item) => {
      if (!isMelee(item)) return;
      hasMelee = true;
      totalCarat += lineCarat(item);
    });

    // Round away float drift so 0.1 x 10 still counts as a full carat.
    totalCarat = Math.round(totalCarat * 1000) / 1000;

    return hasMelee && totalCarat < MIN_TOTAL_CARAT;
  }

  // Re-applies the current state to the DOM. Safe to call as often as needed —
  // controls are re-queried every time because sections get re-rendered under us.
  function apply() {
    CHECKOUT_CONTROLS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((control) => {
        control.disabled = blocked || cartIsEmpty || pendingUpdates > 0;
      });
    });

    DYNAMIC_CHECKOUT_CONTAINERS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((container) => {
        container.classList.toggle('hidden', blocked);

        // These are a second door to checkout, so they have to respect an in-flight update
        // too — but made inert rather than hidden, since collapsing them on every quantity
        // change would jump the layout. pointer-events covers browsers without `inert`.
        const updating = pendingUpdates > 0;
        container.inert = updating;
        container.style.pointerEvents = updating ? 'none' : '';
        container.style.opacity = updating ? '0.5' : '';
      });
    });

    // Keyed off `blocked` only, not the disabled state: an empty cart also
    // disables checkout, but a carat minimum notice would be meaningless there.
    MINIMUM_NOTES.forEach((selector) => {
      document.querySelectorAll(selector).forEach((note) => {
        note.classList.toggle('hidden', !blocked);
      });
    });
  }

  function applyCart(items) {
    cartIsEmpty = items.length === 0;
    blocked = isCartBlocked(items);
    apply();
  }

  function releaseUpdate(watchdog) {
    const index = updateWatchdogs.indexOf(watchdog);
    if (index === -1) return;

    updateWatchdogs.splice(index, 1);
    clearTimeout(watchdog);
    pendingUpdates = Math.max(0, pendingUpdates - 1);
    apply();
  }

  // Held while a cart mutation is in flight, so the state on screen can't be acted on
  // until it has been recalculated. Pair every beginUpdate with an endUpdate.
  function beginUpdate() {
    // Safety net: a caller that never pairs its endUpdate (a handler that threw, a request
    // that hangs) must not leave checkout disabled for the rest of the session.
    const watchdog = setTimeout(() => releaseUpdate(watchdog), UPDATE_WATCHDOG_MS);

    updateWatchdogs.push(watchdog);
    pendingUpdates += 1;
    apply();
  }

  function endUpdate() {
    releaseUpdate(updateWatchdogs[0]);
  }

  function refresh() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    return fetch(cartUrl + '.js')
      .then((response) => response.json())
      .then((cart) => applyCart(cart.items || []))
      .catch(() => {});
  }

  function onCartUpdate(event) {
    const cartData = event && event.cartData;

    if (cartData && Array.isArray(cartData.items)) {
      applyCart(cartData.items);
      return;
    }

    // Add-to-cart responses describe the single added line, not the whole cart, and the
    // popup opens before the follow-up fetch lands. Adding stones only ever raises the
    // total, so the one way an add can newly block the cart is a melee line under the
    // minimum — block on that up front, and let the refresh relax it if the rest of the
    // cart already covers the minimum.
    cartIsEmpty = false;
    if (isMelee(cartData) && lineCarat(cartData) < MIN_TOTAL_CARAT) blocked = true;
    apply();

    refresh();
  }

  window.MeleeCheckoutGate = {
    isBlocked: () => blocked,
    apply,
    applyCart,
    beginUpdate,
    endUpdate,
    refresh,
  };

  document.addEventListener('DOMContentLoaded', () => {
    // Covers controls whose own template doesn't print the gate (e.g. the cart drawer).
    apply();

    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  });
})();
